import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import SEO from '../components/SEO';

// Tier 1 Item 11 — Quick-order pad. B2B reorder shortcut: paste lines, resolve
// against catalog + stock + lifetime-limits, bulk-add to cart in one action.
//
// Accepted line formats (whitespace tolerant):
//   SKU 5
//   SKU,5
//   SKU x5
//   SKU 5  (variant_sku)
export default function QuickOrder() {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [msg, setMsg] = useState('');

  const parseLines = (raw) => {
    return raw.split(/\r?\n/).map(line => line.trim()).filter(Boolean).map(line => {
      // Try several common shapes
      // "SKU,5" or "SKU,5,VARSKU"
      let cols = line.split(/[,\t]/).map(s => s.trim()).filter(Boolean);
      if (cols.length >= 2) {
        // detect 2-col vs 3-col
        return { sku: cols[0], quantity: cols[1], variant_sku: cols[2] || undefined };
      }
      // "SKU x5" or "SKU 5"
      const m = line.match(/^(\S+)\s+x?(\d+)$/i);
      if (m) return { sku: m[1], quantity: m[2] };
      // single token = sku, qty 1
      return { sku: line, quantity: 1 };
    });
  };

  const lineCount = useMemo(() => parseLines(text).length, [text]);

  const resolve = async () => {
    setBusy(true); setMsg(''); setResult(null);
    try {
      const lines = parseLines(text);
      if (!lines.length) return;
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      const r = await fetch('/api/orders/quick-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ lines }),
      });
      const j = await r.json();
      if (!r.ok) { setMsg(j.error || 'Failed'); return; }
      setResult(j);
    } catch (e) { setMsg(e.message); }
    finally { setBusy(false); }
  };

  const addAll = async () => {
    if (!result?.lines?.length) return;
    setBusy(true);
    let ok = 0, capped = 0;
    for (const line of result.lines) {
      try {
        // Synthesize a minimal product object the existing addToCart accepts.
        const r = await addToCart({
          id: line.product_id,
          variant_id: line.variant_id || undefined,
          name: line.name,
          price: line.unit_price,
          stock: line.available_stock,
        }, line.quantity);
        if (r?.ok !== false) ok++;
        if (r?.warning) capped++;
      } catch (_) {}
    }
    setBusy(false);
    setMsg(`Added ${ok}/${result.lines.length} to cart${capped ? ` (${capped} capped)` : ''}.`);
    if (ok > 0) setTimeout(() => navigate('/cart'), 1500);
  };

  const sample = `SET-001,5
SET-002,3
SET-005,12`;

  return (
    <div className="page" style={{ maxWidth: 900, margin: '0 auto', padding: 24 }}>
      <SEO title="Quick order" description="Paste SKUs and quantities to add to cart in bulk." noindex />
      <h1>Quick order</h1>
      <p style={{ color: '#64748b' }}>
        Paste your reorder list below — one SKU per line, with quantity.
        Accepted: <code>SKU,5</code> · <code>SKU 5</code> · <code>SKU x5</code> · <code>SKU,5,VARIANTSKU</code>.
      </p>

      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder={sample}
        rows={10}
        style={{ width: '100%', padding: 12, fontFamily: 'monospace', fontSize: 14, border: '1px solid #e2e8f0', borderRadius: 6 }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
        <small style={{ color: '#64748b' }}>{lineCount} line(s) detected</small>
        <button className="btn btn-primary" onClick={resolve} disabled={busy || !lineCount}>
          {busy ? 'Resolving…' : 'Resolve & preview'}
        </button>
      </div>
      {msg && <div style={{ marginTop: 12, padding: 10, background: '#fff8e1', borderRadius: 4 }}>{msg}</div>}

      {result && (
        <section style={{ marginTop: 24 }}>
          <h2>Preview</h2>
          <p style={{ color: '#64748b' }}>
            {result.summary.resolved} of {result.summary.total_lines} resolved · {result.summary.total_units} units · subtotal ${result.summary.subtotal.toFixed(2)}
            {result.summary.capped_lines > 0 && <> · {result.summary.capped_lines} capped by stock/limit</>}
          </p>

          {result.lines.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead><tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                <th align="left">SKU</th><th align="left">Product</th>
                <th align="right">Qty</th><th align="right">Unit</th><th align="right">Line</th>
              </tr></thead>
              <tbody>
                {result.lines.map((line, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td><code style={{ fontSize: 12 }}>{line.sku}</code>{line.fuzzy_matched && <small style={{ color: '#f59e0b' }}> (fuzzy)</small>}</td>
                    <td>{line.name}</td>
                    <td align="right">
                      <strong>{line.quantity}</strong>
                      {line.capped && <small style={{ color: '#f59e0b', display: 'block' }}>requested {line.requested_quantity}</small>}
                    </td>
                    <td align="right">${Number(line.unit_price).toFixed(2)}</td>
                    <td align="right">${(line.unit_price * line.quantity).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {result.unresolved.length > 0 && (
            <>
              <h3 style={{ marginTop: 16, color: '#dc2626' }}>Unresolved ({result.unresolved.length})</h3>
              <ul>
                {result.unresolved.map((u, i) => (
                  <li key={i}><code>{u.sku}</code> × {u.quantity} — {u.error}</li>
                ))}
              </ul>
            </>
          )}

          <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={addAll} disabled={busy || !result.lines.length}>
              {busy ? 'Adding…' : `Add ${result.lines.length} line(s) to cart`}
            </button>
            <button className="btn" onClick={() => setResult(null)}>Clear</button>
          </div>
        </section>
      )}
    </div>
  );
}
